import Page from "@/containers/Page";
import { currency } from "@/lib/format";
import { usePageInfo } from "@/hooks/usePageInfo";
import { BsChat, BsInfoCircle } from "react-icons/bs";
import { downloadDonations } from "@/services/donations.service";
import { generateFilenameWithDateTime } from "@/lib/utils";
import { useRouter } from "next/router";
import Loader from "../components/Loader";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import { PaginationButton } from "../components/Table";
import Tooltip from "../components/ToolTip";
import { useRecurringPlans } from "@/hooks/useRecurringPlans";
import { RecurringPlan } from "@/types/models/RecurringPlan";
import { usePreloadDonor } from "@/hooks/useDonor";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useState } from "react";

export default function RecurringPlansPage() {
  const [interval, setInterval] = useState<string>();
  const [status, setStatus] = useState<string | undefined>("active");
  const {
    plans,
    page,
    pages,
    next,
    previous,
    canNext,
    canPrevious,
    isLoading,
    total,
    pageSize,
    preloadData,
  } = useRecurringPlans({
    initialPage: 0,
    limit: 10,
    sortBy: "createdAt",
    sortDirection: "DESC",
    status: status ? { in: [status] } : undefined,
    interval: interval ? { in: [interval] } : undefined,
  });
  const { preloadDonor } = usePreloadDonor();

  const router = useRouter();

  const handleDonorClick = (donorId: number) => {
    router.push(`/donors/${donorId}`);
  };

  const routeInfo = usePageInfo();

  return (
    <Page
      title={`${routeInfo.name} | GoGive`}
      header={`${routeInfo.name!} ${!isLoading ? `(${total})` : ""}`}
      Icon={routeInfo.icon!}
    >
      <div className="mb-4 flex items-center gap-6">
        <ToggleGroup
          type="single"
          value={status ?? ""}
          onValueChange={(val) => setStatus(val || undefined)}
          className="justify-start border bg-white  border-border"
        >
          <ToggleGroupItem value="active" variant="default" className="gap-2">
            <span>Active</span>
          </ToggleGroupItem>
          <ToggleGroupItem
            value="incomplete"
            variant="default"
            className="gap-2"
          >
            <span>Pending</span>
          </ToggleGroupItem>
          <ToggleGroupItem value="weekly" variant="default" className="gap-2">
            <span>Cancelled</span>
          </ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup
          type="single"
          value={interval ?? ""}
          onValueChange={(val) => setInterval(val || undefined)}
          className="justify-start"
        >
          <ToggleGroupItem value="monthly" variant="default" className="gap-2">
            <span>Monthly</span>
          </ToggleGroupItem>
          <ToggleGroupItem value="weekly" variant="default" className="gap-2">
            <span>Weekly</span>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="bg-white rounded-sm p-3">
        <table className="min-w-full text-left text-sm font-light">
          <thead className="border-b font-medium border-pink-200">
            <tr className="pb-4 ">
              <th className="px-6 py-4 font-normal text-gray-500">Status</th>
              <th className="px-6 py-4 font-normal text-gray-500">Date </th>
              <th className="px-6 py-4 font-normal text-gray-500">Name</th>
              <th className="px-6 py-4 font-normal text-gray-500">Type</th>
              <th className="px-6 py-4 font-normal text-gray-500">Amount</th>
              <th className="px-6 py-4 font-normal text-gray-500">Donations</th>
              <th className="px-6 py-4 font-normal text-gray-500">Total</th>
              <th className="px-6 py-4 font-normal text-gray-500">Campaign</th>
              <th className=""></th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr
                key={plan.id}
                className="border-b border-pink-100 last:border-b-0 hover:bg-pink-50  transition-all ease-in-out duration-200 hover:cursor-pointer"
                onClick={() =>
                  plan.donorId ? handleDonorClick(plan.donorId) : undefined
                }
                onMouseEnter={() =>
                  plan.donorId ? preloadDonor(plan.donorId) : undefined
                }
              >
                <td className="whitespace-nowrap px-6 py-5 font-normal">
                  {<StatusTag status={plan.status} />}
                </td>
                <td className="whitespace-nowrap px-1 md:px-6 py-5 font-normal text-sm md:text-md text-gray-600 ">
                  {new Date(plan.createdAt).toLocaleString()}
                </td>
                <td className="whitespace-nowrap px-1 md:px-6 py-5 font-semibold text-sm md:text-md text-gray-600 ">
                  {plan.donor && (
                    <>
                      {plan.donor?.company && (
                        <div className="text-gray-500 font-normal">
                          {plan?.donor.company}
                        </div>
                      )}
                      {`${plan.donor.firstName} ${plan.donor.lastName}`}
                    </>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 md:px-6 py-5 font-normal text-sm md:text-md capitalize ">
                  {plan.interval}
                </td>

                <td className="whitespace-nowrap px-6 py-5 font-normal">
                  {currency(plan.grossAmount / 100, 2)}
                </td>
                <td className="whitespace-nowrap px-6 py-5 font-normal">
                  {plan.donationCount}
                </td>
                <td className="whitespace-nowrap px-6 py-5 font-normal">
                  {currency(plan.total / 100, 2)}
                </td>
                <td className="whitespace-nowrap px-6 py-5 font-normal">
                  {plan.campaign?.name ?? ""}
                </td>
                <td className="px-6 py-5 font-normal">
                  <Tooltip
                    content={
                      <div className="min-w-[188px]">{plan.comment}</div>
                    }
                    position="left"
                  >
                    {!!plan.comment && <BsChat size={20} />}
                  </Tooltip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {total > 0 || page > 1 ? (
          <>
            {previous && next && (pages ?? 0) > 1 && (
              <div className="flex justify-end items-center space-x-4">
                <div className="text-sm">{`Page ${page} of ${pages}`}</div>
                <div className="flex ">
                  <PaginationButton
                    onClick={previous}
                    Icon={GoChevronLeft}
                    disabled={!canPrevious}
                    onHover={() => preloadData(-1)}
                  />
                  <PaginationButton
                    onClick={next}
                    Icon={GoChevronRight}
                    disabled={!canNext}
                    onHover={() => preloadData(1)}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-11 text-center text-md text-gray-500">
            {isLoading ? (
              <Loader size={24} />
            ) : (
              <> There are no donations to display yet</>
            )}
          </div>
        )}
      </div>
    </Page>
  );
}

export const StatusTag = ({ status }: { status: RecurringPlan["status"] }) => {
  const activeClass =
    "text-center font-semibold text-xs text-green-900 rounded-full p-1 bg-green-100 border-green-400 border-[1px] px-2 ";
  const pendingClass =
    "text-center font-semibold text-xs text-orange-900 rounded-full p-1 bg-orange-100 border-orange-400 border-[1px] px-2 ";
  const inactiveClass =
    "text-center font-semibold text-xs text-red-900 rounded-full p-1 bg-red-100 border-red-400 border-[1px] px-2 ";
  const style =
    status === "active"
      ? activeClass
      : status === "incomplete"
      ? pendingClass
      : inactiveClass;
  return (
    <div className="flex">
      <div className={style}>
        <div className="">
          <Tooltip
            content={<div className="w-full text-xs">{status}</div>}
            position="right"
          >
            <div className="capitalize text-xs">{status}</div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

const HeaderToolTip = ({ header, tip }: { header: string; tip: string }) => {
  return (
    <div className="flex items-center">
      {header}
      <div className="ml-1">
        <Tooltip
          content={
            <div className="min-w-[144px] text-xs text-center">{tip}</div>
          }
          position="top"
        >
          <BsInfoCircle size={12} />
        </Tooltip>
      </div>
    </div>
  );
};
